import type { EntityConfigurationModel } from "@modelve/api/models/configModels";
import type {
  BaseEntitlementModel,
  EntitlementLimitModel,
  SubscriptionModel,
} from "@modelve/api/models/entitlementsModels";
import type {
  MemberModel,
  PortfolioMemberModel,
} from "@modelve/api/models/memberModels";
import {
  ConnectorResourceResponseModel,
  type ConnectorModel,
  type ConnectorSummaryModel,
  type PortfolioModel,
} from "@modelve/api/models/portfolioModels";
import type { ResourceModel } from "@modelve/api/models/resourceModels";
import type {
  AuditLogInfoModel,
  CheckoutSubscriptionModel,
  CustomerPortalModel,
  PagedAuditLogModel,
  ProductPlanModel,
  TenantConfigModel,
  TenantDomainModel,
  TenantDomainSettingsModel,
  TenantModel,
  TenantSamlIdpMetadataModel,
  TenantSamlSpMetadataModel,
} from "@modelve/api/models/tenantModels";
import type {
  UserLoginHistoryModel,
  UserModel,
} from "@modelve/api/models/userModels";
import {
  auditLogInfoUrl,
  auditLogsUrl,
  billingUrl,
  configurationsUrl,
  connectorsUrl,
  customerPortalUrl,
  domainsUrl,
  featuresUrl,
  idpConfigUrl,
  infoUrl,
  limitsUrl,
  loginUrl,
  memberUrl,
  planUrl,
  portfolioUrl,
  resourcesUrl,
  resourceUrl,
  rootUrl,
  samlSpMetadataUrl,
  settingsUrl,
  subscriptionUrl,
  tenantUrl,
  userUrl,
  verifyUrl,
} from "../apiRoutes";
import { ClientType } from "../routing/clientType";
import { useApiStore } from "../store";
import { defineApiResource } from "./defineApiResource";
import type { ApiInvalidationTarget } from "./types";

const basePortfolioInvalidation = {
  cacheTag: "portfolio",
  clientType: ClientType.Base,
} as const;
const tenantPortfolioInvalidation = {
  cacheTag: "portfolio",
  clientType: ClientType.Tenant,
} as const;
const userResourceInvalidation = {
  cacheTag: "user-resource",
  clientType: ClientType.Base,
} as const;
const tenantMemberInvalidation = {
  cacheTag: "tenant-member",
  clientType: ClientType.Tenant,
} as const;
const currentPortfolioMemberInvalidation = {
  cacheTag: "portfolio-member",
  clientType: ClientType.Portfolio,
} as const;

export const currentTenantApi = defineApiResource<TenantModel>({
  domainUrl: tenantUrl,
  cacheTag: "tenant",
  clientType: ClientType.Tenant,
  detailRoute: () => rootUrl,
  invalidates: {
    put: [
      tenantPortfolioInvalidation,
      basePortfolioInvalidation,
      userResourceInvalidation,
    ],
  },
});

export const currentPortfolioApi = defineApiResource<PortfolioModel>({
  domainUrl: portfolioUrl,
  cacheTag: "portfolio",
  clientType: ClientType.Portfolio,
  detailRoute: () => rootUrl,
  invalidates: {
    put: [
      basePortfolioInvalidation,
      tenantPortfolioInvalidation,
      userResourceInvalidation,
    ],
  },
});

export const currentUserApi = defineApiResource<UserModel>({
  domainUrl: userUrl,
  cacheTag: "user",
  clientType: ClientType.Base,
  detailRoute: () => userUrl,
});

export const currentUserResourcesApi = defineApiResource<ResourceModel>({
  domainUrl: currentUserApi.domainUrl,
  cacheTag: "user-resource",
  clientType: ClientType.Base,
  detailRoute: () => resourceUrl,
});

export const currentUserLoginHistoryApi =
  defineApiResource<UserLoginHistoryModel>({
    domainUrl: currentUserApi.domainUrl,
    cacheTag: "user-login-history",
    clientType: ClientType.Base,
    detailRoute: () => `${currentUserApi.detail().route}/${loginUrl}`,
  });

export const billingProductPlansApi = defineApiResource<ProductPlanModel[]>({
  domainUrl: tenantUrl,
  cacheTag: "billing",
  clientType: ClientType.Base,
  detailRoute: () => `${tenantUrl}/${billingUrl}/${planUrl}`,
});

export const billingSubscriptionCheckoutApi =
  defineApiResource<CheckoutSubscriptionModel>({
    domainUrl: billingProductPlansApi.domainUrl,
    cacheTag: "subscription",
    clientType: ClientType.Base,
    detailRoute: () => `${tenantUrl}/${billingUrl}/${subscriptionUrl}`,
  });

export const portfolioApi = defineApiResource<PortfolioModel>({
  domainUrl: portfolioUrl,
  cacheTag: "portfolio",
  clientType: ClientType.Base,
  collectionRoute: () => portfolioUrl,
  detailRoute: (id) => `${portfolioUrl}/${id}`,
  getEntityKey: (portfolio) => portfolio.id,
});

export const tenantPortfolioApi = defineApiResource<PortfolioModel>({
  domainUrl: portfolioApi.domainUrl,
  cacheTag: "portfolio",
  clientType: ClientType.Tenant,
  collectionRoute: () => portfolioUrl,
  detailRoute: (id) => `${portfolioUrl}/${id}`,
  getEntityKey: (portfolio) => portfolio.id,
  invalidates: {
    post: [basePortfolioInvalidation, userResourceInvalidation],
  },
});

export const tenantMemberApi = defineApiResource<MemberModel>({
  domainUrl: memberUrl,
  cacheTag: "tenant-member",
  clientType: ClientType.Tenant,
  putCacheStrategy: "revalidate",
  collectionRoute: () => memberUrl,
  detailRoute: (userId) => `${memberUrl}/${userId}`,
  getEntityKey: (member) => member.userId,
});

export const currentPortfolioMemberApi =
  defineApiResource<PortfolioMemberModel>({
    domainUrl: tenantMemberApi.domainUrl,
    cacheTag: "portfolio-member",
    clientType: ClientType.Portfolio,
    collectionRoute: () => memberUrl,
    detailRoute: (memberId) => `${memberUrl}/${memberId}`,
    getEntityKey: (member) => member.userId,
    invalidates: {
      post: currentPortfolioMembershipInvalidations,
      delete: currentPortfolioMembershipInvalidations,
    },
  });

function currentPortfolioMembershipInvalidations(): readonly ApiInvalidationTarget[] {
  const invalidations: ApiInvalidationTarget[] = [tenantMemberInvalidation];
  const portfolioId = useApiStore.getState().portfolioId;
  if (portfolioId != null) {
    invalidations.push(tenantPortfolioMemberApi.collection(portfolioId));
  }
  return invalidations;
}

export const portfolioConnectorApi = defineApiResource<ConnectorModel>({
  domainUrl: connectorsUrl,
  cacheTag: "portfolio-connector",
  clientType: ClientType.Portfolio,
  collectionRoute: () => connectorsUrl,
  detailRoute: (connectorId) => `${connectorsUrl}/${connectorId}`,
  getEntityKey: (connector) => connector.id,
});

export const portfolioConnectorSummaryApi =
  defineApiResource<ConnectorSummaryModel>({
    domainUrl: portfolioConnectorApi.domainUrl,
    cacheTag: "portfolio-connector",
    clientType: ClientType.Portfolio,
    collectionRoute: () => connectorsUrl,
    detailRoute: (connectorId) =>
      portfolioConnectorApi.detail(connectorId).route,
    getEntityKey: (connector) => connector.id,
  });

export const portfolioConnectorResourcesApi =
  defineApiResource<ConnectorResourceResponseModel>({
    domainUrl: `${connectorsUrl}/${resourcesUrl}`,
    cacheTag: "portfolio-connector-resources",
    clientType: ClientType.Portfolio,
    detailRoute: () => `${connectorsUrl}/${resourcesUrl}`,
  });

export const portfolioConnectorExistingResourcesApi = defineApiResource<
  ConnectorResourceResponseModel,
  [connectorId: string]
>({
  domainUrl: `${connectorsUrl}/${resourcesUrl}`,
  cacheTag: "portfolio-connector-resources",
  clientType: ClientType.Portfolio,
  detailRoute: (connectorId) =>
    `${connectorsUrl}/${connectorId}/${resourcesUrl}`,
});

export const tenantPortfolioMemberApi = defineApiResource<
  PortfolioMemberModel,
  [portfolioId: string]
>({
  domainUrl: portfolioApi.domainUrl,
  cacheTag: "portfolio-member",
  clientType: ClientType.Base,
  collectionRoute: (portfolioId) =>
    `${portfolioApi.detail(portfolioId).route}/${memberUrl}`,
  detailRoute: (portfolioId, memberId) =>
    `${portfolioApi.detail(portfolioId).route}/${memberUrl}/${memberId}`,
  getEntityKey: (member) => member.userId,
  invalidates: {
    put: [tenantMemberInvalidation, currentPortfolioMemberInvalidation],
    delete: [tenantMemberInvalidation, currentPortfolioMemberInvalidation],
  },
});

export const tenantInfoApi = defineApiResource<EntityConfigurationModel>({
  domainUrl: configurationsUrl,
  cacheTag: "tenant-info",
  clientType: ClientType.Tenant,
  detailRoute: () => `${configurationsUrl}/${infoUrl}`,
});

export const tenantConfigApi = defineApiResource<TenantConfigModel>({
  domainUrl: configurationsUrl,
  cacheTag: "tenant-config",
  clientType: ClientType.Tenant,
  detailRoute: () => configurationsUrl,
});

export const tenantDomainApi = defineApiResource<TenantDomainModel>({
  domainUrl: domainsUrl,
  cacheTag: "tenant-domain",
  clientType: ClientType.Tenant,
  collectionRoute: () => domainsUrl,
  detailRoute: (domainName) => `${domainsUrl}/${domainName}`,
  getEntityKey: (domain) => domain.domainName,
  invalidates: {
    delete: [
      "tenant-domain-saml-idp",
      "tenant-domain-settings",
      "tenant-domain-saml-sp-metadata",
    ],
  },
});

export const tenantDomainSamlIdpApi = defineApiResource<
  TenantSamlIdpMetadataModel,
  [domainName: string]
>({
  domainUrl: tenantDomainApi.domainUrl,
  cacheTag: "tenant-domain-saml-idp",
  clientType: ClientType.Tenant,
  detailRoute: (domainName) =>
    `${tenantDomainApi.detail(domainName).route}/${idpConfigUrl}`,
  invalidates: { put: ["tenant-domain"] },
});

export const tenantDomainVerifyApi = defineApiResource<
  void,
  [domainName: string]
>({
  domainUrl: tenantDomainApi.domainUrl,
  cacheTag: "tenant-domain",
  clientType: ClientType.Tenant,
  detailRoute: (domainName) =>
    `${tenantDomainApi.detail(domainName).route}/${verifyUrl}`,
});

export const tenantDomainSettingsApi = defineApiResource<
  TenantDomainSettingsModel,
  [domainName: string]
>({
  domainUrl: tenantDomainApi.domainUrl,
  cacheTag: "tenant-domain-settings",
  clientType: ClientType.Tenant,
  detailRoute: (domainName) =>
    `${tenantDomainApi.detail(domainName).route}/${settingsUrl}`,
  invalidates: { put: ["tenant-domain"] },
});

export const tenantDomainSamlSpMetadataApi = defineApiResource<
  TenantSamlSpMetadataModel,
  [domainName: string]
>({
  domainUrl: tenantDomainApi.domainUrl,
  cacheTag: "tenant-domain-saml-sp-metadata",
  clientType: ClientType.Tenant,
  detailRoute: (domainName) =>
    `${tenantDomainApi.detail(domainName).route}/${samlSpMetadataUrl}`,
});

export const tenantSubscriptionApi = defineApiResource<SubscriptionModel>({
  domainUrl: subscriptionUrl,
  cacheTag: "subscription",
  clientType: ClientType.Tenant,
  detailRoute: () => subscriptionUrl,
});

export const tenantFeaturesApi = defineApiResource<BaseEntitlementModel[]>({
  domainUrl: featuresUrl,
  cacheTag: "feature",
  clientType: ClientType.Tenant,
  detailRoute: () => featuresUrl,
});

export const portfolioLimits = defineApiResource<EntitlementLimitModel[]>({
  domainUrl: limitsUrl,
  cacheTag: "portfolio-limits",
  clientType: ClientType.Portfolio,
  detailRoute: () => limitsUrl,
});

export const portfolioFeaturesApi = defineApiResource<BaseEntitlementModel[]>({
  domainUrl: tenantFeaturesApi.domainUrl,
  cacheTag: "feature",
  clientType: ClientType.Portfolio,
  detailRoute: () => featuresUrl,
});

export const customerPortalApi = defineApiResource<CustomerPortalModel>({
  domainUrl: billingUrl,
  cacheTag: "billing",
  clientType: ClientType.Tenant,
  detailRoute: () => `${billingUrl}/${customerPortalUrl}`,
});

export const auditLogApi = defineApiResource<PagedAuditLogModel>({
  domainUrl: auditLogsUrl,
  cacheTag: "audit-log",
  clientType: ClientType.Tenant,
  detailRoute: () => auditLogsUrl,
});

export const auditLogInfoApi = defineApiResource<AuditLogInfoModel>({
  domainUrl: auditLogApi.domainUrl,
  cacheTag: "audit-log",
  clientType: ClientType.Tenant,
  detailRoute: () => auditLogInfoUrl,
});
